import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  NgZone,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';

/**
 * Int-only floating panel with a switch for the GrowthBook debug overlay
 * (flag ids as tooltips/outlines) and a list of the ids of every flag evaluated
 * via `FeatureFlagService.isOn()` (menu, `*featureFlag`, route guards and plain
 * TS usages). Also shows a FAIL-OPEN badge while GrowthBook is unavailable and
 * every flag is treated as ON. Renders nothing on qa / prod.
 * Keyboard shortcut: Alt + Shift + F toggles the overlay.
 */
@Component({
  selector: 'eop-feature-flag-debug-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:keydown)': 'onKeydown($event)' },
  templateUrl: './feature-flag-debug-panel.component.html',
  styleUrls: ['./feature-flag-debug-panel.component.scss'],
})
export class FeatureFlagDebugPanelComponent implements OnInit {
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly REFRESH_MS = 1000;

  readonly available = this.featureFlagService.debugAvailable === true;
  overlayOn = this.featureFlagService.debugEnabled === true;
  expanded = false;
  /** GrowthBook unavailable → every flag is treated as ON. */
  failOpen = false;
  filterText = '';
  /** Ids of every flag evaluated via `isOn()`, sorted. */
  flags: string[] = [];
  filteredFlags: string[] = [];

  private signature = '';

  ngOnInit(): void {
    if (!this.available) {
      return;
    }
    this.featureFlagService.debugEnabled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        this.overlayOn = enabled;
        this.cdr.markForCheck();
      });

    this.featureFlagService.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.failOpen = this.featureFlagService.unavailable === true;
      this.cdr.markForCheck();
      this.refresh();
    });

    // `isOn()` is called during change detection, so poll the registry
    // outside Angular and only re-render when something actually changed.
    this.zone.runOutsideAngular(() => {
      const id = setInterval(() => this.refresh(), FeatureFlagDebugPanelComponent.REFRESH_MS);
      this.destroyRef.onDestroy(() => clearInterval(id));
    });
  }

  /** Switch the overlay (tooltips/outlines in the whole app) on or off. */
  setOverlay(enabled: boolean): void {
    this.featureFlagService.setDebugEnabled(enabled);
  }

  /** Alt + Shift + F toggles the overlay. */
  onKeydown(event: KeyboardEvent): void {
    if (this.available && event.altKey && event.shiftKey && event.code === 'KeyF') {
      event.preventDefault();
      this.setOverlay(!this.overlayOn);
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.cdr.markForCheck();
  }

  onFilter(event: Event): void {
    this.filterText = (event.target as HTMLInputElement).value ?? '';
    this.applyFilter();
    this.cdr.markForCheck();
  }

  copy(id: string): void {
    navigator.clipboard?.writeText(id).catch(() => undefined);
  }

  refresh(): void {
    const flags = [...this.featureFlagService.evaluatedFlags.keys()].sort((a, b) =>
      a.localeCompare(b)
    );
    const signature = flags.join(',');
    if (signature === this.signature) {
      return;
    }
    this.signature = signature;
    this.zone.run(() => {
      this.flags = flags;
      this.applyFilter();
      this.cdr.markForCheck();
    });
  }

  private applyFilter(): void {
    const term = this.filterText.trim().toLowerCase();
    this.filteredFlags = term ? this.flags.filter(id => id.toLowerCase().includes(term)) : this.flags;
  }
}
